import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { handleApiError, logError, createApiRequest } from '../utils/errorHandler';
import { logger } from '../utils/logger';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role?: 'user' | 'admin' | 'super_admin';
  subscriptionStatus?: string | null;
  subscriptionPeriodEnd?: string | null;
  hasActiveSubscription?: boolean;
  isInGracePeriod?: boolean;
  displayStatus?: string;
  trialDaysRemaining?: number;
  isEligibleForTrial?: boolean;
  hasHadTrial?: boolean;
  stripeCustomerId?: string;
  createdAt?: string;
}

interface LoginResult {
  success: boolean;
  isFirstLogin: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  register: (email: string, password: string, firstName: string, lastName: string, marketingEmailsOptIn?: boolean) => Promise<boolean>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  isAuthenticated: boolean;
  refreshSubscriptionStatus: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasActiveSubscription: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  const refreshSubscriptionStatus = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/subscription-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUser(prevUser => prevUser ? {
          ...prevUser,
          subscriptionStatus: data.subscriptionStatus,
          subscriptionPeriodEnd: data.subscriptionPeriodEnd,
          hasActiveSubscription: data.hasActiveSubscription,
          isInGracePeriod: data.isInGracePeriod,
          displayStatus: data.displayStatus
        } : null);
      }
    } catch (error) {
      logger.componentError('AuthContext', error, { action: 'refreshSubscriptionStatus' });
      // Don't update state with potentially stale data on error
      throw error;
    }
  }, []); // Remove user dependency to prevent infinite loop

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/verify-token`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user); // Replace entire user object
      }
    } catch (error) {
      logger.componentError('AuthContext', error, { action: 'refreshUser' });
      throw error;
    }
  }, []);

  const verifyToken = useCallback(async (token: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/verify-token`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData.user);
        // User data from token verification should be current
      } else if (response.status === 429) {
        // Rate limited - don't logout, just show warning
        console.warn('Rate limited during token verification - keeping user logged in');
        // Keep current user state, don't remove token
      } else {
        localStorage.removeItem('token');
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('token');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Check for existing token on mount
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token with backend
      verifyToken(token);
    } else {
      setIsLoading(false);
    }
  }, [verifyToken]);

  const login = async (email: string, password: string): Promise<LoginResult> => {
    try {
      const data = await createApiRequest(`${process.env.REACT_APP_API_URL}/api/login`, {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      localStorage.setItem('token', data.token);
      setUser(data.user);
      // Refresh subscription status after successful login if needed
      if (data.user && !data.user.subscriptionStatus) {
        refreshSubscriptionStatus().catch(err => logger.warn('Failed to refresh subscription after login', err));
      }
      return {
        success: data.success,
        isFirstLogin: data.isFirstLogin
      };
    } catch (error) {
      const errorInfo = handleApiError(error);
      logError(error, 'AuthContext.login');
      throw errorInfo; // Let the component handle showing the error
    }
  };

  const register = async (email: string, password: string, firstName: string, lastName: string, marketingEmailsOptIn: boolean = false): Promise<boolean> => {
    try {
      const data = await createApiRequest(`${process.env.REACT_APP_API_URL}/api/register`, {
        method: 'POST',
        body: JSON.stringify({ email, password, firstName, lastName, marketingEmailsOptIn }),
      });

      localStorage.setItem('token', data.token);
      setUser(data.user);
      return true;
    } catch (error) {
      const errorInfo = handleApiError(error);
      logError(error, 'AuthContext.register');
      throw errorInfo; // Let the component handle showing the error
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    // Clear all React Query cache to prevent data leakage between users
    queryClient.clear();
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
    }
  };

  const isAuthenticated = !!user;
  const hasActiveSubscription = user?.hasActiveSubscription || false;

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      login,
      register,
      logout,
      updateUser,
      isAuthenticated,
      refreshSubscriptionStatus,
      refreshUser,
      hasActiveSubscription
    }}>
      {children}
    </AuthContext.Provider>
  );
};